import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  AuditReportOptions,
  AuditReportPage,
  ReportNavigation,
  ReportSelection,
} from "src/types/federation-audit-report";

export const AUDIT_REPORT_PATH =
  "/dashboard/organiser/federation-audit-reports";
const base = (provider: number) =>
  `/providers/${provider}/federation/audit/reports`;

export function useAuditReportOptions(provider: number, enabled: boolean) {
  const { user } = useAuthContext();
  return useSWR<AuditReportOptions>(
    enabled && provider > 0 && user?.id
      ? `${base(provider)}/options#actor-${user.id}`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}

export async function queryAuditReport(
  provider: number,
  selection: ReportSelection,
  cursor: string | null,
  signal: AbortSignal,
) {
  return (
    await axios.post<AuditReportPage>(
      `${base(provider)}/query`,
      { ...selection, limit: 50, ...(cursor && { cursor }) },
      { signal },
    )
  ).data;
}

export async function auditReportActors(
  provider: number,
  selection: ReportSelection,
  search: string,
  signal: AbortSignal,
) {
  return (
    await axios.post<{ items: { id: number }[]; has_more: boolean }>(
      `${base(provider)}/actor-options`,
      { ...selection, search },
      { signal },
    )
  ).data;
}

export async function downloadAuditReport(
  provider: number,
  selection: ReportSelection,
  signal: AbortSignal,
) {
  try {
    return (
      await axios.post<Blob>(`${base(provider)}/export`, selection, {
        responseType: "blob",
        signal,
      })
    ).data;
  } catch (error) {
    // The shared Axios interceptor can unwrap error responses into a Blob.
    if (error instanceof Blob) {
      let detail: unknown;
      try {
        detail = JSON.parse(await error.text());
      } catch {
        detail = { detail: "audit_report_unavailable" };
      }
      throw detail;
    }
    throw error;
  }
}

export function auditReportError(error: unknown): string {
  const value = error as {
    detail?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  const detail = value?.detail ?? value?.response?.data?.detail;
  if (typeof detail === "string") {
    if (detail.includes("cursor_invalid")) return "ar_cursor_error";
    if (detail.includes("row_limit") || detail.includes("byte_limit"))
      return "ar_limit_error";
    if (
      detail.includes("denied") ||
      detail.includes("permission") ||
      detail.includes("capability") ||
      (detail.includes("unavailable") && value?.response?.status === 403)
    )
      return "ar_denied";
    if (detail.includes("target_not_found")) return "ar_missing";
    if (detail.includes("invalid")) return "ar_invalid";
  }
  return "ar_error";
}

export function auditReportTarget(
  target: ReportNavigation,
  provider: number,
): string | null {
  if (
    target.provider_id !== provider ||
    !Number.isSafeInteger(target.id) ||
    target.id <= 0
  )
    return null;
  const root = "/dashboard/organiser";
  if (target.kind === "entries") {
    if (
      !Number.isSafeInteger(target.tournament_id) ||
      Number(target.tournament_id) <= 0 ||
      !Number.isSafeInteger(target.category_id) ||
      Number(target.category_id) <= 0
    )
      return null;
    return `${root}/tournament/${target.tournament_id}/applications?category_id=${target.category_id}&office_source=${target.id}&office_kind=entries&office_provider=${provider}`;
  }
  if (target.kind === "competitions")
    return Number.isSafeInteger(target.tournament_id) &&
      Number(target.tournament_id) > 0
      ? `${root}/tournament/${target.tournament_id}/applications`
      : null;
  const routes: Partial<Record<ReportNavigation["kind"], string>> = {
    assignments: `${root}/federation-assignments`,
    organisations: "/dashboard/federation-organisations",
    registrations: `${root}/federation-players`,
    players: `${root}/federation-players`,
    licences: `${root}/federation-licences`,
    transfers: `${root}/federation-transfers`,
    finance: `${root}/federation-finance`,
  };
  const query = new URLSearchParams({
    office_source: String(target.id),
    office_kind: target.kind,
    office_provider: String(provider),
  });
  return routes[target.kind] ? `${routes[target.kind]}?${query}` : null;
}
